<div class="cb-workbench">
    <!-- Workbench Header Info -->
    <div class="workbench-info">
        <p-message severity="info">
            <ng-template pTemplate="content">
                <div class="info-content">
                    <i class="fa-regular fa-circle-info mr-2"></i>
                    <span>
                        <strong>CB Configuration Workbench</strong> - Configure the Cognitive Backend to understand your domain.
                        Document entities, define concepts and synonyms, create query templates, and manage the AI training pipeline.
                    </span>
                </div>
            </ng-template>
        </p-message>
    </div>

    <!-- Toolbar -->
    <div class="workbench-toolbar">
        <div class="toolbar-left">
            <!-- Navigation Tabs -->
            <div class="nav-tabs">
                @for (item of navigationItems; track item.id) {
                    <div
                        class="nav-tab"
                        [class.active]="activeView === item.id"
                        [pTooltip]="item.tooltip"
                        tooltipPosition="bottom"
                        (click)="switchView(item.id)">
                        <i [class]="item.icon"></i>
                        <span>{{ item.label }}</span>
                    </div>
                }
            </div>
        </div>

        <div class="toolbar-right">
            <!-- Statistics Summary -->
            <div class="stats-summary">
                <div class="stat-item">
                    <span class="stat-value" [class]="'severity-' + getCoverageSeverity(overallCoverage)">
                        {{ overallCoverage }}%
                    </span>
                    <span class="stat-label">Overall Coverage</span>
                </div>
                <div class="stat-item">
                    <span class="stat-value">{{ documentedEntities }}/{{ totalEntities }}</span>
                    <span class="stat-label">Entities Documented</span>
                </div>
                <div class="stat-item">
                    <span class="stat-value">{{ totalModules }}</span>
                    <span class="stat-label">Modules</span>
                </div>
            </div>

            <!-- Actions -->
            <p-button
                icon="fa-regular fa-rotate"
                label="Refresh"
                [text]="true"
                severity="secondary"
                (onClick)="refreshCoverage()"
                [loading]="loading">
            </p-button>
        </div>
    </div>

    <!-- Main Content -->
    <div class="workbench-content">
        <!-- View-specific explanations -->
        <div class="view-explanation">
            @switch (activeView) {
                @case (CbWorkbenchView.coverage) {
                    <div class="explanation-card">
                        <i class="fa-regular fa-lightbulb"></i>
                        <div class="explanation-text">
                            <strong>Schema Coverage</strong> shows which entities have documentation for the AI to understand.
                            <span class="explanation-detail">Click on a module to expand it, then double-click an entity to add or edit its documentation. Higher coverage = better AI query understanding.</span>
                        </div>
                    </div>
                }
                @case (CbWorkbenchView.concepts) {
                    <div class="explanation-card">
                        <i class="fa-regular fa-lightbulb"></i>
                        <div class="explanation-text">
                            <strong>Domain Concepts</strong> define business terms and their synonyms so the AI understands your domain language.
                            <span class="explanation-detail">Example: "Active Contract" with synonyms "Running Contract", "Valid Contract". Double-click a concept to edit, or click "Add Concept" to create new ones.</span>
                        </div>
                    </div>
                }
                @case (CbWorkbenchView.templates) {
                    <div class="explanation-card">
                        <i class="fa-regular fa-lightbulb"></i>
                        <div class="explanation-text">
                            <strong>Query Templates</strong> are pre-defined QL patterns for common questions.
                            <span class="explanation-detail">Templates match natural language patterns (e.g., "show contracts for customer") and generate ready-to-use QL queries with extracted parameters.</span>
                        </div>
                    </div>
                }
                @case (CbWorkbenchView.rules) {
                    <div class="explanation-card">
                        <i class="fa-regular fa-lightbulb"></i>
                        <div class="explanation-text">
                            <strong>Business Rules</strong> define reusable filter conditions applied to queries.
                            <span class="explanation-detail">Example: "Active Contracts Only" rule adds status=ACTIVE filter automatically when querying contracts. Rules can be referenced in templates.</span>
                        </div>
                    </div>
                }
                @case (CbWorkbenchView.training) {
                    <div class="explanation-card">
                        <i class="fa-regular fa-lightbulb"></i>
                        <div class="explanation-text">
                            <strong>Training Pipeline</strong> uses user feedback to continuously improve AI accuracy.
                            <span class="explanation-detail">Review captured examples, approve good ones for training, and monitor model versions. More training data = smarter AI responses.</span>
                        </div>
                    </div>
                }
                @case (CbWorkbenchView.tenantSync) {
                    <div class="explanation-card">
                        <i class="fa-regular fa-lightbulb"></i>
                        <div class="explanation-text">
                            <strong>Tenant Sync</strong> synchronizes CB documentation from the master tenant to this tenant.
                            <span class="explanation-detail">Creates new items, updates existing ones, and removes orphaned entries. Business descriptions marked as "locked" will be preserved during sync.</span>
                        </div>
                    </div>
                }
            }
        </div>

        @switch (activeView) {
            @case (CbWorkbenchView.coverage) {
                <cb-schema-coverage
                    [coverageData]="coverageData"
                    [loading]="loading"
                    (entitySelected)="onEntitySelected($event)"
                    (refreshRequested)="refreshCoverage()">
                </cb-schema-coverage>
            }
            @case (CbWorkbenchView.concepts) {
                <cb-domain-concept-graph
                    [concepts]="concepts"
                    [synonyms]="synonyms"
                    [loading]="conceptsLoading"
                    (conceptSelected)="onConceptSelected($event)"
                    (conceptDoubleClicked)="onConceptDoubleClicked($event)"
                    (addConceptRequested)="onAddConceptRequested()">
                </cb-domain-concept-graph>
            }
            @case (CbWorkbenchView.templates) {
                <cb-query-template-builder></cb-query-template-builder>
            }
            @case (CbWorkbenchView.rules) {
                <cb-business-rule-builder></cb-business-rule-builder>
            }
            @case (CbWorkbenchView.training) {
                <cb-training-dashboard></cb-training-dashboard>
            }
            @case (CbWorkbenchView.tenantSync) {
                <cb-tenant-sync-panel
                    (syncCompleted)="refreshCoverage()">
                </cb-tenant-sync-panel>
            }
        }
    </div>


    <!-- Concept Editor Drawer -->
    <p-drawer
        [(visible)]="showConceptEditor"
        position="right"
        [style]="{width: '500px'}"
        (onHide)="closeConceptEditor()">
        <ng-template pTemplate="header">
            <span class="drawer-header">
                <i class="fa-regular fa-book"></i>
                {{ isNewConcept ? 'Add Domain Concept' : 'Edit Domain Concept' }}
            </span>
        </ng-template>

        <cb-concept-editor
            [concept]="selectedConcept"
            [isNew]="isNewConcept"
            (saved)="onConceptSaved($event)"
            (cancelled)="closeConceptEditor()">
        </cb-concept-editor>
    </p-drawer>
</div>
